import * as Yup from 'yup';

import { EBankAccountType, EPaymentMethod, EVerificationMethod } from '@/config/payment';

import type { TAddPaymentMethodForm } from '@/types/wallet';

import {
  makeEnumString,
  makeSelectString,
  requiredNumber,
  requiredString,
} from '@/validation/common';

const paymentMethodActions = ['selectExisting', 'addNew'] as const;

const createPaymentMethodSchema = Yup.object({
  method: makeEnumString(EPaymentMethod).default(EPaymentMethod.ACH),
  verification: makeEnumString(EVerificationMethod).default(EVerificationMethod.INSTANT),
  bankAccount: Yup.object().when('verification', {
    is: EVerificationMethod.MANUAL,
    then: Yup.object({
      bankAccountType: makeEnumString(EBankAccountType),
      name: requiredString.max(255, { key: 'errors.form.maxLabel', max: 255 }),
      routingNumber: requiredString.matches(/^\d{9}$/, {
        message: {
          key: 'errors.form.nDigits',
          n: 9,
        },
      }),
      accountNumber: requiredString.matches(/^\d{5,17}$/, {
        message: {
          key: 'errors.form.rangeDigits',
          min: 5,
          max: 17,
        },
      }),
    }),
  }),
}).required();

const selectPaymentMethodSchema = Yup.object({
  paymentMethodId: requiredString,
}).required();

export const paymentMethodSchema = Yup.object({
  action: makeSelectString(paymentMethodActions).nullable().default(null),
  data: Yup.object()
    .default({})
    .when('action', {
      is: 'addNew',
      then: createPaymentMethodSchema,
    })
    .when('action', {
      is: 'selectExisting',
      then: selectPaymentMethodSchema,
    }) as Yup.SchemaOf<TAddPaymentMethodForm['data']>,
}).required();

const verificationAmount = requiredNumber.positive('errors.form.positive').lessThan(0.1, {
  key: 'errors.form.lessThan',
  value: '0.10',
});

export const paymentMethodVerificationSchema = Yup.object({
  amount1: verificationAmount,
  amount2: verificationAmount,
}).required();
